import React, { useState, useEffect } from 'react';
import { SessionProvider, useSession } from './contexts/SessionContext';
import SessionGuard from './components/SessionGuard';
import SessionStatus from './components/SessionStatus';
// PortalSelection removed from unauthenticated flow
import AdminLogin from './components/AdminLogin';
import AdminSidebar from './components/AdminSidebar';
import AdminNavbar from './components/AdminNavbar';
import AdminDashboard from './components/AdminDashboard';
import AdminVendors from './components/AdminVendors';
import AdminPayouts from './components/AdminPayouts';
import AdminInventoryManagement from './components/AdminInventoryManagement';
import AdminProducts from './components/AdminProducts';
import AdminKYCReview from './components/AdminKYCReview';
import AdminPricing from './components/AdminPricing';
import AdminOrders from './components/AdminOrders';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Register from './components/Register';
import LocationSetup from './components/LocationSetup';
import KYCUpload from './components/KYCUpload';
import KYCProgress from './components/KYCProgress';
import Dashboard from './components/Dashboard';
import Vendors from './components/Vendors';
import Products from './components/Products';
import Orders from './components/Orders';
import OrderManagement from './components/OrderManagement';
import Reports from './components/Reports';
import Payouts from './components/Payouts';
import BankDetails from './components/BankDetails';
import Settings from './components/Settings';
import AdminSettings from './components/AdminSettings';
import InventorySidebar from './components/InventorySidebar';
import InventoryDashboard from './components/InventoryDashboard';
import StockLevels from './components/StockLevels';
import Suppliers from './components/Suppliers';
import Invoices from './components/Invoices';
import InventoryReports from './components/InventoryReports';
import VendorKYC from './components/VendorKYC';
import KYCDashboard from './components/KYCDashboard';
import KYCDocumentUpload from './components/KYCDocumentUpload';
import KYCTimeline from './components/KYCTimeline';
import './App.css';

const AppContent = () => {
  const { isAuthenticated, user, login, logout } = useSession();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState('vendor-login');
  const [vendorData, setVendorData] = useState(null);

  // Update current page based on authentication status
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.type === 'admin') {
        setCurrentPage('admin-dashboard');
      } else if (user.type === 'vendor') {
        setCurrentPage('vendor-dashboard');
      } else if (user.type === 'inventory') {
        setCurrentPage('inventory-dashboard');
      }
    } else {
      setCurrentPage('portal-selection');
    }
  }, [isAuthenticated, user]);

  // Simple global navigation event to switch tabs from widgets
  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail?.tab;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  // Track viewport to detect mobile
  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const shouldLock = isMobile && !sidebarCollapsed;
      document.body.style.overflow = shouldLock ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isMobile, sidebarCollapsed]);

  // Lightweight hash-based routing for separate login links
  useEffect(() => {
    const applyHashRoute = () => {
      const hash = (window.location.hash || '').replace('#', '').replace('/', '');
      if (hash === 'admin-login') {
        setCurrentPage('admin-login');
        return;
      }
      if (hash === 'vendor-login') {
        setCurrentPage('vendor-login');
        return;
      }
      // Default unauthenticated route to vendor login
      if (!isAuthenticated) {
        setCurrentPage('vendor-login');
        if (window.location.hash !== '#/vendor-login') {
          window.location.hash = '/vendor-login';
        }
      }
    };
    applyHashRoute();
    window.addEventListener('hashchange', applyHashRoute);
    return () => window.removeEventListener('hashchange', applyHashRoute);
  }, [isAuthenticated]);

  const handleAdminLogin = (adminData) => {
    login({
      email: adminData.email,
      type: 'admin'
    });
  };

  const handleVendorLogin = (vendorData) => {
    login({
      email: vendorData.email,
      type: 'vendor'
    });
    
    // Store vendor-specific data
    localStorage.setItem('vendorData', JSON.stringify(vendorData));
    if (vendorData.token) {
      localStorage.setItem('token', vendorData.token);
    }
    
    // Set appropriate page based on vendor status
    const nextPage = vendorData.status === 'APPROVED' ? 'vendor-dashboard' : 'kyc-progress';
    setCurrentPage(nextPage);
  };

  const handleVendorRegister = (data) => {
    // Clear any existing vendor data
    setVendorData(null);
    // Redirect to login page after successful registration
    setCurrentPage('vendor-login');
  };

  const handleLocationSet = (locationData) => {
    setVendorData(prev => ({
      ...prev,
      location: locationData
    }));
    setCurrentPage('kyc-upload');
  };

  const handleLocationSkip = () => {
    setCurrentPage('kyc-upload');
  };

  const handleKYCComplete = (kycData) => {
    setVendorData(prev => ({
      ...prev,
      kyc: kycData
    }));
    setCurrentPage('kyc-progress');
  };

  const handleKYCSkip = () => {
    setCurrentPage('kyc-progress');
  };

  const handleKYCProgressComplete = () => {
    setIsLoggedIn(true);
    setUserEmail(vendorData.email);
    setUserType('vendor');
    setCurrentPage('vendor-dashboard');
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userEmail', vendorData.email);
    localStorage.setItem('userType', 'vendor');
  };

  const handleKYCEdit = (step) => {
    if (step === 'registration') {
      setCurrentPage('vendor-register');
    } else if (step === 'location') {
      setCurrentPage('location-setup');
    } else if (step === 'kyc') {
      setCurrentPage('kyc-upload');
    }
  };

  const handleInventoryLogin = (email) => {
    // Inventory portal removed; redirect to admin login instead
    handleAdminLogin(email);
  };

  // Direct navigation helpers (if needed elsewhere)
  const handleSelectAdmin = () => {
    window.location.hash = '/admin-login';
    setCurrentPage('admin-login');
  };

  const handleSelectVendor = () => {
    window.location.hash = '/vendor-login';
    setCurrentPage('vendor-login');
  };

  const handleSwitchToVendorRegister = () => {
    setCurrentPage('vendor-register');
  };

  const handleSwitchToVendorLogin = () => {
    setCurrentPage('vendor-login');
  };

  const handleSwitchToAdminLogin = () => {
    setCurrentPage('admin-login');
  };

  // handleLogout removed - now using SessionContext logout

  const renderAdminContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'vendors':
        return <AdminVendors />;
      case 'inventory':
        return <AdminInventoryManagement />;
      case 'pricing':
        return <AdminPricing />;
      case 'kyc':
        return <AdminKYCReview />;
      case 'products':
        return <AdminProducts />;
      case 'orders':
        return <OrderManagement />;
      case 'reports':
        return <Reports />;
      case 'payouts':
        return <AdminPayouts />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard />;
    }
  };

  const renderVendorContent = () => {
    const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
    const vendorId = vendorData.id;

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products />;
      case 'inventory':
        return <InventoryDashboard />;
      case 'orders':
        return <OrderManagement />;
      case 'kyc':
        return vendorId ? <KYCDashboard vendorId={vendorId} /> : <VendorKYC />;
      case 'payouts':
        return <Payouts />;
      case 'bank-details':
        return <BankDetails />;
      case 'profile':
        return <Settings />; // Using Settings component for Profile
      default:
        return <Dashboard />;
    }
  };

  // Inventory portal removed; inventory features merged under vendor/admin

  // Show login/register pages if not authenticated (no main portal)
  if (!isAuthenticated) {
    if (currentPage === 'admin-login') {
      return <AdminLogin onLogin={handleAdminLogin} onSwitchToVendor={handleSelectVendor} />;
    }
    if (currentPage === 'vendor-login') {
      return <Login onLogin={handleVendorLogin} onSwitchToRegister={handleSwitchToVendorRegister} onSwitchToAdmin={handleSwitchToAdminLogin} />;
    }
    if (currentPage === 'vendor-register') {
      return <Register onRegister={handleVendorRegister} onSwitchToLogin={handleSwitchToVendorLogin} />;
    }
    if (currentPage === 'location-setup') {
      return <LocationSetup onLocationSet={handleLocationSet} onSkip={handleLocationSkip} initialAddress={vendorData?.shopAddress} vendorId={vendorData?.id} />;
    }
    if (currentPage === 'kyc-upload') {
      return <KYCUpload onKYCComplete={handleKYCComplete} onSkip={handleKYCSkip} vendorId={vendorData?.id} />;
    }
    if (currentPage === 'kyc-progress') {
      return <KYCProgress vendorData={vendorData} onComplete={handleKYCProgressComplete} onEdit={handleKYCEdit} />;
    }
    // Fallback to vendor login
    window.location.hash = '/vendor-login';
    return <Login onLogin={handleVendorLogin} onSwitchToRegister={handleSwitchToVendorRegister} onSwitchToAdmin={handleSwitchToAdminLogin} />;
  }

  // Render admin portal
  if (user?.type === 'admin') {
    return (
      <SessionGuard requiredUserType="admin">
        <div className="app">
          <AdminSidebar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            collapsed={sidebarCollapsed}
            setCollapsed={setSidebarCollapsed}
          />
          {isMobile && !sidebarCollapsed && (
            <div
              className="mobile-overlay active"
              onClick={() => setSidebarCollapsed(true)}
            />
          )}
          <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <AdminNavbar 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
              userEmail={user.email}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
              sidebarCollapsed={sidebarCollapsed}
            >
              <SessionStatus />
            </AdminNavbar>
            <div className="content">
              {renderAdminContent()}
            </div>
          </div>
        </div>
      </SessionGuard>
    );
  }

  // Inventory portal removed

  // Render vendor portal
  return (
    <SessionGuard requiredUserType="vendor">
      <div className="app">
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          onLogout={logout}
        />
        {isMobile && !sidebarCollapsed && (
          <div
            className="mobile-overlay active"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Navbar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            userEmail={user?.email}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
          >
            <SessionStatus />
          </Navbar>
          <div className="content">
            {renderVendorContent()}
          </div>
        </div>
      </div>
    </SessionGuard>
  );
}

function App() {
  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}

export default App;
